import { Icon } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { useActionBarResponsiveClasses } from './useActionBarResponsiveClasses';

interface ActionBarToggleProps {
    icon: Icon;
    activeIcon?: Icon;
    indeterminateIcon?: Icon;
    label: string;
    isActive: boolean;
    indeterminate?: boolean;
    onClick: () => void;
    slideContent?: ReactNode;
}

export function ActionBarToggle({
    icon: IconComponent,
    activeIcon: ActiveIconComponent,
    indeterminateIcon: IndeterminateIconComponent,
    label,
    isActive,
    indeterminate = false,
    onClick,
    slideContent,
}: ActionBarToggleProps) {
    const responsive = useActionBarResponsiveClasses();
    const showIndeterminate = indeterminate && !isActive;
    const IconToRender =
        isActive && ActiveIconComponent ? ActiveIconComponent : IconComponent;

    return (
        <div className="flex items-center gap-1">
            <button
                onClick={onClick}
                aria-label={label}
                className={cn(
                    'group relative flex items-center gap-1.5 h-8 rounded-md',
                    responsive.trailingPadding,
                )}
            >
                <span className="relative inline-flex size-4 flex-shrink-0 items-center justify-center">
                    {showIndeterminate && IndeterminateIconComponent ? (
                        <>
                            <IconComponent
                                className={cn(
                                    'size-4 text-grey-300 group-hover:text-grey-200',
                                )}
                            />
                            <span className="absolute inset-0 flex items-center justify-center">
                                <IndeterminateIconComponent className="size-2.5 text-grey-100" />
                            </span>
                        </>
                    ) : (
                        <IconToRender
                            className={cn(
                                'size-4',
                                isActive
                                    ? 'text-grey-100'
                                    : 'text-grey-300 group-hover:text-grey-200',
                            )}
                        />
                    )}
                    {isActive && !showIndeterminate && (
                        <span
                            className="pointer-events-none absolute bottom-0 right-0 w-[3px] h-[3px] rounded-full bg-white-100"
                            aria-hidden="true"
                        />
                    )}
                </span>
                <span
                    className={cn(
                        'text-sm font-medium whitespace-nowrap',
                        responsive.label,
                        isActive || showIndeterminate
                            ? 'text-grey-100'
                            : 'text-grey-300 group-hover:text-grey-200',
                    )}
                >
                    {label}
                </span>
            </button>

            <AnimatePresence>
                {isActive && slideContent && (
                    <motion.div
                        initial={{ width: 0, opacity: 0 }}
                        animate={{ width: 'auto', opacity: 1 }}
                        exit={{ width: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden whitespace-nowrap mr-3"
                    >
                        {slideContent}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
